import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IncomeEmploymentFields } from "./IncomeEmploymentFields";
import { IncomeFreelanceFields } from "./IncomeFreelanceFields";
import { IncomeBenefitsFields } from "./IncomeBenefitsFields";
import { ApplicationIncomeType, PayFrequency } from "../../types";
import { ApplicationIncomeInput } from "../../api/services";

export type IncomeType = "employment" | "freelance" | "benefits";
interface Props {
  /** If provided, the form will initialize with these values (for editing). */
  initial?: ApplicationIncomeInput;
  /** Called with the filled-out data when the user submits. */
  onSave: (data: ApplicationIncomeInput) => void;
  /** Called when the user cancels out of the form. */
  onCancel: () => void;
}

const IncomeEntryForm: React.FC<Props> = ({ initial, onSave, onCancel }) => {
  const { t } = useTranslation();

  // Top-level "type" selector
  const [type, setType] = useState<IncomeType>(initial?.type ?? "employment");

  // Shared fields
  const [company, setCompany] = useState(initial?.company ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [jobTitle, setJobTitle] = useState(initial?.jobTitle ?? "");
  const [monthlyNetIncome, setMonthlyNetIncome] = useState<number>(
    initial?.monthlyNetIncome ?? 0
  );
  const [startDate, setStartDate] = useState(initial?.startDate ?? "");
  const [current, setCurrent] = useState<boolean>(initial?.current ?? false);
  const [endDate, setEndDate] = useState(initial?.endDate ?? "");
  const [payFrequency, setPayFrequency] = useState(initial?.payFrequency ?? "");

  // Benefits-only field
  const [benefitType, setBenefitType] = useState(initial?.description ?? "");

  // When the user switches "type", reset all other fields
  useEffect(() => {
    setCompany("");
    setAddress("");
    setPhone("");
    setJobTitle("");
    setMonthlyNetIncome(0);
    setStartDate("");
    setCurrent(false);
    setEndDate("");
    setPayFrequency("");
    setBenefitType("");
  }, [type]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Basic validation (required fields by type)
    if (!company.trim()) {
      alert(t("income.errors.companyRequired"));
      return;
    }
    if (!startDate) {
      alert(t("income.errors.startDateRequired"));
      return;
    }
    if (!current && !endDate) {
      alert(t("income.errors.endDateRequired"));
      return;
    }
    if (monthlyNetIncome <= 0) {
      alert(t("income.errors.monthlyIncomeRequired"));
      return;
    }
    if ((type === "freelance" || type === "benefits") && !payFrequency) {
      alert(t("income.errors.payFrequencyRequired"));
      return;
    }
    if (type === "benefits" && !benefitType) {
      alert(t("income.errors.benefitTypeRequired"));
      return;
    }

    // Build payload
    const payload: ApplicationIncomeInput = {
      type: type as ApplicationIncomeType,
      company,
      address: type === "employment" ? address : undefined,
      phone: type === "employment" ? phone : undefined,
      jobTitle: type === "benefits" ? benefitType : jobTitle,
      monthlyNetIncome,
      startDate,
      current,
      endDate: current ? undefined : endDate,
      payFrequency:
        type === "employment" ? undefined : (payFrequency as PayFrequency),
      description: type === "benefits" ? benefitType : undefined,
    };

    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Income Type Selector */}
      <div className="mb-4">
        <label htmlFor="incomeType" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.typeLabel")} <span className="text-danger">*</span>
        </label>
        <select
          id="incomeType"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={type}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setType(e.target.value as IncomeType)
          }
          required
        >
          <option value="employment">{t("income.types.employment")}</option>
          <option value="freelance">{t("income.types.freelance")}</option>
          <option value="benefits">{t("income.types.benefits")}</option>
        </select>
      </div>

      {/* Employment Fields */}
      {type === "employment" && (
        <IncomeEmploymentFields
          company={company}
          address={address}
          phone={phone}
          jobTitle={jobTitle}
          monthlyNetIncome={monthlyNetIncome}
          startDate={startDate}
          current={current}
          endDate={endDate}
          onCompanyChange={setCompany}
          onAddressChange={setAddress}
          onPhoneChange={setPhone}
          onJobTitleChange={setJobTitle}
          onMonthlyIncomeChange={setMonthlyNetIncome}
          onStartDateChange={setStartDate}
          onCurrentToggle={() => setCurrent((prev) => !prev)}
          onEndDateChange={setEndDate}
        />
      )}

      {/* Freelance/Gig Fields */}
      {type === "freelance" && (
        <IncomeFreelanceFields
          jobTitle={jobTitle}
          company={company}
          startDate={startDate}
          current={current}
          endDate={endDate}
          payFrequency={payFrequency}
          monthlyNetIncome={monthlyNetIncome}
          onJobTitleChange={setJobTitle}
          onCompanyChange={setCompany}
          onStartDateChange={setStartDate}
          onCurrentChange={setCurrent}
          onEndDateChange={setEndDate}
          onPayFrequencyChange={setPayFrequency}
          onMonthlyIncomeChange={setMonthlyNetIncome}
        />
      )}

      {/* Benefits/Other Income Fields */}
      {type === "benefits" && (
        <IncomeBenefitsFields
          benefitType={benefitType}
          company={company}
          startDate={startDate}
          current={current}
          endDate={endDate}
          payFrequency={payFrequency}
          monthlyNetIncome={monthlyNetIncome}
          onBenefitTypeChange={setBenefitType}
          onCompanyChange={setCompany}
          onStartDateChange={setStartDate}
          onCurrentChange={setCurrent}
          onEndDateChange={setEndDate}
          onPayFrequencyChange={setPayFrequency}
          onMonthlyIncomeChange={setMonthlyNetIncome}
        />
      )}

      {/* Error message area can be added here if desired */}

      {/* Buttons */}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          className="mr-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          onClick={onCancel}
        >
          {t("income.buttons.cancel")}
        </button>
        <button type="submit" className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light">
          {initial ? t("income.buttons.save") : t("income.buttons.submit")}
        </button>
      </div>
    </form>
  );
};

export default IncomeEntryForm;
